import type { ComponentProps } from 'react';
import type { Icon } from '@wistia/ui';
import { SUPPORT_URL } from '@lib/constants';

/**
 * Data for the `top_nav_simplification` test arm: Platform and Learn
 * dropdowns plus a static Pricing link. Desktop and mobile rows show the
 * name only; link descriptions are not rendered.
 *
 * Tracking: every link fires a Snowplow `link`/`click` structured event with
 * `trackingLabel` + `trackingProperty` ('main-nav'). Labels match the control
 * nav wherever the destination is the same, so the two arms compare
 * directly; only genuinely new items get new labels (`product-overview`,
 * `product-build-with-ai`, `product-mochine-promo`).
 */
const TRACKING_PROPERTY = 'main-nav';

export type NavLink = {
  href: string;
  name: string;
  /** not rendered on link rows; the featured card shows its own */
  description: string;
  icon: ComponentProps<typeof Icon>['type'];
  trackingLabel: string;
  trackingProperty: string;
};

/**
 * The featured card in a dropdown's right-hand column. `poster` shows at
 * rest; on hover/focus (desktop) `previewSrc` plays a short, muted loop. A
 * plain silent `<video>` of a delivery asset, not a Wistia player.
 */
export type NavFeatured = Omit<NavLink, 'icon'> & {
  poster: string;
  previewSrc?: string;
  previewSeconds?: number;
};

/** The highlighted first row of the Platform dropdown (desktop only). */
export type NavOverview = {
  href: string;
  name: string;
  trackingLabel: string;
  trackingProperty: string;
};

export type NavPromo = {
  href: string;
  title: string;
  description: string;
  trackingLabel: string;
  trackingProperty: string;
};

export type NavMenu = {
  label: string;
  /** className hook for the menu’s accent colour */
  id: 'platform' | 'learn' | 'pricing';
  href: string;
  trackingLabel: string;
  trackingProperty: string;
  /** Present on dropdown menus only */
  links?: NavLink[];
  overview?: NavOverview;
  featured?: NavFeatured;
  promo?: NavPromo;
};

export const MENU_DATA: NavMenu[] = [
  {
    label: 'Platform',
    id: 'platform',
    href: '/product',
    trackingLabel: 'product-menu',
    trackingProperty: TRACKING_PROPERTY,
    overview: {
      href: '/product',
      name: 'Platform overview',
      trackingLabel: 'product-overview',
      trackingProperty: TRACKING_PROPERTY,
    },
    links: [
      {
        href: '/product/hosting',
        name: 'Host videos',
        icon: 'player',
        description: 'Host and embed video on a player built for your brand.',
        trackingLabel: 'product-hosting',
        trackingProperty: TRACKING_PROPERTY,
      },
      {
        href: '/product/webinar-hosting',
        name: 'Run webinars',
        icon: 'live',
        description: 'Live and on-demand webinars that drive leads.',
        trackingLabel: 'product-webinar-hosting',
        trackingProperty: TRACKING_PROPERTY,
      },
      {
        href: '/product/video-recording',
        name: 'Make videos',
        icon: 'camera',
        description: 'Record, edit, and repurpose videos in your browser.',
        trackingLabel: 'product-recording',
        trackingProperty: TRACKING_PROPERTY,
      },
      {
        href: '/product/analytics',
        name: 'Measure impact',
        icon: 'stats',
        description: 'Know who watched and what your videos drove.',
        trackingLabel: 'product-analytics',
        trackingProperty: TRACKING_PROPERTY,
      },
      {
        href: '/integrations',
        name: 'Integrate',
        icon: 'integrations',
        description: 'Viewer data in HubSpot, Marketo, Pardot, and more.',
        trackingLabel: 'integrations-menu',
        trackingProperty: TRACKING_PROPERTY,
      },
      {
        href: '/product/mcp-server',
        name: 'Build with AI',
        icon: 'sparkle',
        description: 'Run Wistia from Claude, Cursor, or ChatGPT.',
        trackingLabel: 'product-build-with-ai',
        trackingProperty: TRACKING_PROPERTY,
      },
    ],
    featured: {
      href: '/demo',
      name: 'Watch a demo',
      description: 'See how all of Wistia works together.',
      trackingLabel: 'watch-a-demo-nav',
      trackingProperty: TRACKING_PROPERTY,
      // 2025 Wistia Product Demo (9mz11isa6g), the /demo hero video
      poster:
        'https://embed-ssl.wistia.com/deliveries/173273dcc4df399d236915194c9eed6f.jpg?image_crop_resized=800x450',
      previewSrc:
        'https://embed-ssl.wistia.com/deliveries/325e30f49985bfb2cd727644f1125f9dbd9e7dab.bin',
    },
    promo: {
      href: 'https://mochine.ai',
      title: 'Try Mochine',
      description: 'Fix your boring talking head videos.',
      trackingLabel: 'product-mochine-promo',
      trackingProperty: TRACKING_PROPERTY,
    },
  },
  {
    label: 'Learn',
    id: 'learn',
    href: '/blog',
    trackingLabel: 'learning-center-menu',
    trackingProperty: TRACKING_PROPERTY,
    links: [
      {
        href: '/blog',
        name: 'Blog',
        icon: 'write',
        description: 'Tips and insights to get more from video marketing.',
        trackingLabel: 'learning-center-blog',
        trackingProperty: TRACKING_PROPERTY,
      },
      {
        href: '/watch',
        name: 'Watch',
        icon: 'play',
        description: 'Tutorials, original series, and webinars on demand.',
        trackingLabel: 'learning-center-watch',
        trackingProperty: TRACKING_PROPERTY,
      },
      {
        href: '/resources/free-downloads',
        name: 'Resources',
        icon: 'download',
        description: 'Free music, templates, and tools for your next video.',
        trackingLabel: 'learning-center-asset-library',
        trackingProperty: TRACKING_PROPERTY,
      },
      {
        href: '/blog/category/customer-stories',
        name: 'Customers',
        icon: 'people',
        description: 'See how teams like yours get results with Wistia.',
        trackingLabel: 'learning-center-customer-stories',
        trackingProperty: TRACKING_PROPERTY,
      },
      {
        href: SUPPORT_URL,
        name: 'Help center',
        icon: 'help-center',
        description: 'How-tos and answers from our support team.',
        trackingLabel: 'learning-center-help-center',
        trackingProperty: TRACKING_PROPERTY,
      },
      {
        href: 'https://docs.wistia.com/',
        name: 'Developer docs',
        icon: 'docs-dev',
        description: 'Build custom experiences with Wistia’s APIs.',
        trackingLabel: 'learning-center-developer-docs',
        trackingProperty: TRACKING_PROPERTY,
      },
    ],
    featured: {
      href: '/explore/state-of-video-report?utm_source=storefront&utm_medium=top-nav&utm_campaign=state-of-video-2026',
      name: '2026 State of Video Report',
      description: 'Strategy insights from 13 million+ videos.',
      trackingLabel: 'learning-center-state-of-video-2026',
      trackingProperty: TRACKING_PROPERTY,
      // 2026 State of Video Live Show (x10zt19irk)
      poster:
        'https://embed-ssl.wistia.com/deliveries/fb997d83ea9762b667047c98bdb442e9.jpg?image_crop_resized=800x450',
      previewSrc:
        'https://embed-ssl.wistia.com/deliveries/311bd20abc6e498e1ecdeec34093cf8fdfd1fe7f.bin',
    },
  },
  {
    label: 'Pricing',
    id: 'pricing',
    href: '/pricing',
    trackingLabel: 'pricing-nav',
    trackingProperty: TRACKING_PROPERTY,
  },
];

// Local menu timing respects the visitor’s reduced-motion preference.
export const navMotionVars = `
  --nav-ease: cubic-bezier(0.23, 1, 0.32, 1);
  --nav-caret-ease: cubic-bezier(0.6, 0, 0.2, 0.5);
  --nav-label-ease: cubic-bezier(0.45, 0.05, 0.55, 0.95);
  --nav-row-duration: 200ms;
  --nav-panel-duration: 240ms;
  --nav-caret-duration: 140ms;
  --nav-label-duration: 240ms;

  @media (prefers-reduced-motion: reduce) {
    --nav-row-duration: 0.01ms;
    --nav-panel-duration: 0.01ms;
    --nav-caret-duration: 0.01ms;
    --nav-label-duration: 0.01ms;
  }
`;
